import React, { useContext, useRef, useState } from 'react';
import Table from 'react-bootstrap/Table';
import DeleteButton from '../Generic/DeleteButton';
import OrderModal from './OrderModal';
import { deleteOrder, getOrderDetailsLazy } from '../../clients/order_client';
import { ORDER_HARD_DELETE_OPTION } from './orderHardDeleteOption';
import { FileEarmarkPdfFill } from 'react-bootstrap-icons';
import S3MediaLink from '../Generic/S3MediaLink';
import QuoteDetailModal from '../Quote/QuoteDetailModal';
import OrderDetailModal from './OrderDetailModal';
import OrderFundingDisplay from './OrderFundingDisplay';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import { Accordion } from 'react-bootstrap';
import ProductDetailModal from '../Product/ProductDetailModal';
import { OrderDeletionContext, AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import {
  formatDateStr,
  getAssociatedUserDisplay,
} from '../../config_and_helpers/helpers';
import { logError } from '../../config_and_helpers/logger';
import SortableHeaderCell from '../Generic/SortableHeaderCell';
import OrderFileLinksCell from './OrderFileLinksCell';

/**
 * Represents a table component for displaying order details.
 *
 * @param {Object} orderTable - The order table component.
 * @param {Array} orderList - An array of order objects.
 * @param {function} handleDelete - A callback function for editing an order.
 * @param {function} clearSearchValue - A function for clearing the search value.
 * @returns {JSX.Element} - The rendered table component.
 */
const OrderTable = ({
  orderList,
  handleDelete,
  clearSearchValue,
  onProjectUpdate,
  sortBy = '',
  sortDirection = 'asc',
  onSort = null,
}) => {
  // Fetches the isOrderDeleted context to manage follow-up actions
  const { toggleOrderDeleted } = useContext(OrderDeletionContext);
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  // State to store orders with full data for editing
  const [ordersWithFullData, setOrdersWithFullData] = useState({});
  const [loadingOrders, setLoadingOrders] = useState({});

  const [fetchedQuotes, setFetchedQuotes] = useState([]); // State to store the suppliers being fetch to be used
  // thus preventing unnecessary requests
  const fetchedQuotesRef = useRef(new Set()); // Ref storing the quote Ids to be used to prevent requests
  // Callback function on order deletion

  const onOrderDelete = () => {
    toggleOrderDeleted();
    handleDelete();
  };

  // Function to fetch full order data for editing
  const fetchFullOrderData = async orderId => {
    // Don't fetch if already loading or already fetched
    if (loadingOrders[orderId] || ordersWithFullData[orderId]) {
      return;
    }

    setLoadingOrders(prev => ({ ...prev, [orderId]: true }));

    try {
      const fullOrderData = await getOrderDetailsLazy(token, orderId);

      setOrdersWithFullData(prev => ({
        ...prev,
        [orderId]: fullOrderData,
      }));
    } catch (error) {
      logError('OrderTable', 'Error fetching full order data', error);
    } finally {
      setLoadingOrders(prev => ({ ...prev, [orderId]: false }));
    }
  };

  // Function to get the best available order data (full data if available, otherwise list data)
  const getOrderDataForEditing = order => {
    return ordersWithFullData[order.id] || order;
  };

  return (
    <Table striped bordered hover>
      <thead>
        {/* Table Headers */}
        <tr className="text-center">
          <th>#</th>
          <SortableHeaderCell
            label="ID"
            sortKey="order_num"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>Quote / Quote File</th>
          <th>Arrival</th>
          <th>PO</th>
          <th>Receipt / invoice</th>
          <th>Supplier</th>
          <SortableHeaderCell
            label="Status"
            sortKey="status"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>Order Reference</th>
          <SortableHeaderCell
            label="Funding"
            sortKey="funding"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHeaderCell
            label="User"
            sortKey="user"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {!isViewer ? <th>Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {/* Mapping through orderList to create several rows in the table. */}
        {orderList.map((order, index) => {
          // With lazy loading, order.items might not be available in the list
          // We'll handle this in the detail modal instead
          const isOrderFulfilled = order.items
            ? order.items.every(item => item.status === 'OK')
            : false;

          return (
            <React.Fragment key={order.id}>
              {/* Each row of order */}
              <tr className="text-center align-middle">
                {/* Various columns like quote, arrival time, receipts etc. */}
                {/* Action Buttons for each order */}
                <td>{index + 1}</td>
                <td>
                  <OrderDetailModal
                    orderObj={order}
                    orderId={order.id}
                    updateOrders={handleDelete}
                  />
                </td>
                <td style={{ width: '200px' }}>
                  <div className="d-flex flex-row justify-content-around">
                    <QuoteDetailModal
                      fetchedQuotesRef={fetchedQuotesRef}
                      fetchedQuotes={fetchedQuotes}
                      setFetchedQuotes={setFetchedQuotes}
                      quoteId={order.quote.id}
                    />
                    <S3MediaLink
                      url={order.quote?.quote_url}
                      quoteId={order.quote?.id}
                      title="Open quote PDF"
                    >
                      <FileEarmarkPdfFill size={'1.5rem'} />
                    </S3MediaLink>
                  </div>
                </td>
                <td>
                  {order.reception_dates && order.reception_dates.length > 0 ? (
                    order.reception_dates.map((date, index) => (
                      <div key={index}>
                        {formatDateStr(date)}
                        {index < order.reception_dates.length - 1 && <br />}
                      </div>
                    ))
                  ) : (
                    <span className="text-muted">No dates</span>
                  )}
                </td>
                <td>
                  <OrderFileLinksCell
                    order={order}
                    category="po"
                  />
                </td>
                <td>
                  <OrderFileLinksCell
                    order={order}
                    category="receipt"
                  />
                </td>
                <td>
                  <SupplierDetailModal
                    supplierObj={order.supplier}
                    supplierId={order.supplier.id}
                    smallerFont={true}
                  />
                </td>
                <td>{isOrderFulfilled ? 'Fulfilled' : order.quote?.status}</td>
                <td>{order.corporate_order_ref}</td>
                <td>
                  <OrderFundingDisplay order={order} className="small" />
                </td>
                <td>
                  {(() => {
                    const display = getAssociatedUserDisplay(order);
                    return display ? (
                      <span>{display}</span>
                    ) : (
                      <span className="text-muted">-</span>
                    );
                  })()}
                </td>
                {!isViewer ? (
                <td className="d-flex flex-row justify-content-center">
                  <div className="me-2">
                    <OrderModal
                      orderObj={getOrderDataForEditing(order)}
                      clearSearchValue={clearSearchValue}
                      onEditButtonHover={() => fetchFullOrderData(order.id)}
                      isEditButtonLoading={loadingOrders[order.id]}
                      projectId={order.project} // Pass project ID if order is associated with a project
                      onProjectUpdate={onProjectUpdate} // Pass refresh callback if provided
                      onOrderUpdated={(orderId, updatedOrder) => {
                        setOrdersWithFullData(prev => ({
                          ...prev,
                          [orderId]: updatedOrder,
                        }));
                      }}
                    />
                  </div>
                  <DeleteButton
                    objectType="order"
                    objectName={order.id}
                    objectId={order.id}
                    deleteFetchFunc={deleteOrder}
                    onSuccessfulDelete={onOrderDelete}
                    clearSearchValue={clearSearchValue}
                    hardDeleteOption={ORDER_HARD_DELETE_OPTION}
                  />
                </td>
                ) : null}
              </tr>
              {/* Order Items are displayed in a collapsible accordion - only show if items are available */}
              {order.items && order.items.length > 0 && (
                <tr>
                  {/* Order Items are displayed in a collapsible accordion */}
                  <td></td>
                  <td colSpan={5}>
                    <Accordion>
                      <Accordion.Item eventKey={0}>
                        <Accordion.Header>Order Items</Accordion.Header>
                        <Accordion.Body>
                          <Table striped bordered hover>
                            <thead>
                              <tr className="text-center bold-italic-text">
                                <td>#</td>
                                <td>Product</td>
                                <td>Quantity</td>
                                <td>Status</td>
                                <td>Details</td>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item, itemIndex) => {
                                return (
                                  <React.Fragment key={itemIndex}>
                                    <tr className="text-center italic-text">
                                      <td>{itemIndex + 1}</td>
                                      <td key={itemIndex}>
                                        <ProductDetailModal
                                          productObj={item.product}
                                          productId={item.product.id}
                                        />
                                      </td>
                                      <td>{item.quantity}</td>
                                      <td>{item.status}</td>
                                      <td>{item.issue_detail}</td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};
export default OrderTable;
